import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Loader, Btn, Field, TextInput, TextArea, SelectInput } from "../components/Primitives";
import { toast } from "sonner";

export default function OrgOnboarding() {
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    api.get("/org/profile").then((r) => setProfile(r.data)).catch(() => {});
  }, []);

  if (!profile) return <Loader />;

  const set = (k, v) => setProfile((p) => ({ ...p, [k]: v }));
  const setList = (k, v) => setProfile((p) => ({ ...p, [k]: String(v).split(",").map(x => x.trim()).filter(Boolean) }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/org/profile", profile);
      setProfile(data); toast.success("Profile saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const complete = async () => {
    await save();
    await api.post("/org/complete-onboarding");
    toast.success("Onboarding complete");
    window.location.href = "/app/dashboard";
  };

  const steps = [
    "Organization",
    "Contact & DPO",
    "Processing Profile",
    "Systems",
    "Review",
  ];

  return (
    <div>
      <PageHeader
        title="Organization Onboarding"
        description="Set up your organization profile and DPDP applicability inputs."
        testId="onboarding-page-header"
      />

      <div className="mb-6 flex items-center gap-2 flex-wrap">
        {steps.map((s, i) => (
          <button
            key={s}
            data-testid={`step-btn-${i}`}
            onClick={() => setStep(i)}
            className={`text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded-md border ${step === i ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}
          >
            {i + 1}. {s}
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-6">
        {step === 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Organization name" required><TextInput data-testid="org-name" value={profile.name || ""} onChange={(e) => set("name", e.target.value)} /></Field>
            <Field label="Industry"><TextInput data-testid="org-industry" value={profile.industry || ""} onChange={(e) => set("industry", e.target.value)} /></Field>
            <Field label="Website"><TextInput data-testid="org-website" value={profile.website || ""} onChange={(e) => set("website", e.target.value)} /></Field>
            <Field label="Registered office"><TextInput data-testid="org-office" value={profile.registered_office || ""} onChange={(e) => set("registered_office", e.target.value)} /></Field>
            <Field label="Employee count"><TextInput data-testid="org-employees" type="number" value={profile.employee_count || ""} onChange={(e) => set("employee_count", Number(e.target.value))} /></Field>
            <Field label="Business units (comma-separated)" className="sm:col-span-2"><TextInput data-testid="org-units" value={(profile.business_units || []).join(", ")} onChange={(e) => setList("business_units", e.target.value)} /></Field>
            <Field label="Locations (comma-separated)" className="sm:col-span-2"><TextInput data-testid="org-locations" value={(profile.locations || []).join(", ")} onChange={(e) => setList("locations", e.target.value)} /></Field>
          </div>
        )}
        {step === 1 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="DPO name"><TextInput data-testid="dpo-name" value={profile.dpo_name || ""} onChange={(e) => set("dpo_name", e.target.value)} /></Field>
            <Field label="DPO email"><TextInput data-testid="dpo-email" value={profile.dpo_email || ""} onChange={(e) => set("dpo_email", e.target.value)} /></Field>
            <Field label="DPO phone"><TextInput data-testid="dpo-phone" value={profile.dpo_phone || ""} onChange={(e) => set("dpo_phone", e.target.value)} /></Field>
          </div>
        )}
        {step === 2 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              ["processes_digital_pd", "Processes digital personal data"],
              ["collects_online", "Collects data online"],
              ["digitizes_offline", "Digitizes offline data"],
              ["processes_children_data", "Processes children's data"],
              ["processes_sensitive_data", "Processes sensitive/high-risk data"],
              ["transfers_outside_india", "Transfers data outside India"],
              ["uses_processors", "Uses data processors/vendors"],
              ["uses_automated_decisioning", "Uses automated decisioning / profiling / AI"],
            ].map(([k, l]) => (
              <label key={k} className="flex items-center gap-2 text-sm border border-slate-200 rounded-md p-3">
                <input data-testid={`toggle-${k}`} type="checkbox" checked={!!profile[k]} onChange={(e) => set(k, e.target.checked)} />
                <span className="text-slate-700">{l}</span>
              </label>
            ))}
          </div>
        )}
        {step === 3 && (
          <Field label="Existing systems (comma-separated)">
            <TextArea data-testid="org-systems" value={(profile.systems || []).join(", ")} onChange={(e) => setList("systems", e.target.value)} />
          </Field>
        )}
        {step === 4 && (
          <div className="text-sm">
            <div className="text-slate-700 mb-3">Review your inputs and complete onboarding.</div>
            <pre className="bg-slate-50 border border-slate-200 rounded-md p-4 text-[11px] overflow-x-auto">{JSON.stringify(profile, null, 2)}</pre>
          </div>
        )}

        <div className="mt-6 flex justify-between border-t border-slate-200 pt-4">
          <Btn variant="secondary" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}>Back</Btn>
          {step < steps.length - 1 ? (
            <Btn data-testid="onboarding-next-btn" onClick={async () => { await save(); setStep(step + 1); }}>Save & Continue</Btn>
          ) : (
            <Btn data-testid="onboarding-complete-btn" onClick={complete} disabled={saving}>Complete Onboarding</Btn>
          )}
        </div>
      </div>
    </div>
  );
}
